import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { Spinner, Tooltip } from '@patternfly/react-core';
import { EllipsisVIcon } from '@patternfly/react-icons';
import { sortable, cellWidth } from '@patternfly/react-table';
import { PatternflyTable } from '@app/components/PatternflyTable';
import { PageSection, Title } from "@patternfly/react-core";
import { useSiteContext } from '../../contexts/site.context';
import { controllerSelectors, serverSelectors, siteSelectors } from '@app/common/configuration/configuration.selectors';
import { useSearch } from '@app/lib/use-search';
import { push } from 'connected-react-router';
import { ErrorView } from '../error/error';
import { actionCreators } from '@app/common/configuration/configuration.reducer';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  getSortedRowModel,
  Updater,
  Column,
} from '@tanstack/react-table';
import { MdArrowDownward, MdArrowUpward, MdCircle, MdUnfoldMore } from 'react-icons/md';
import { Button, Card, CardBody, Col, Row, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import GoogleMapWithBubblesSites from '@app/components/GoogleMapWithBubblesSites';
import Loader from '@app/components/Loader';
import Swal from 'sweetalert2';
import { useEffect } from 'react';
import SiteApi from '@app/api/site-api/site.actions';
import { useMapCache } from '@app/hooks/useMapCache';
import UserSession from '@app/common/user-session';
import { selectMetricsUrl } from '@app/common/session/session.selectors';

type ColumnFiltersState = ColumnFilter[];
interface ColumnFilter {
  id: string;
  value: unknown;
}

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

// Status color mapping to match site view
const statusColors: Record<number, string> = {
  0: "#000000", // Unknown - Black (Site has never checked in)
  1: "#ff0000", // Down - Red (Site has checked in but not within the last two minutes)
  2: "#FBC02D", // Warning - Yellow (Site has checked in within the last two minutes but one or more links are not connected)
  3: "#45B649", // Good - Green (Site has checked in within the last two minutes and all links are connected)
  4: "#808080"  // Missing - Gray
};

// Status text mapping for tooltips
const statusTexts: Record<number, string> = {
  0: "Offline - Site has never been powered on",
  1: "Down - Site is down", 
  2: "Degraded - One or more links are not connected",
  3: "Online - Site is online and all links are connected",
  4: "Offline - Site has been offline for more than 2 weeks"
};

const SiteActionsMenu = ({ site, basePath, isManager, onReboot, onPerformance, onDelete }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)} direction="start">
      <DropdownToggle
        color="link"
        className="list-actions-toggle"
        aria-label={`Actions for ${site.name}`}
      >
        <EllipsisVIcon />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem tag={Link} to={`${basePath}/site/edit/${site.id}`}>Edit</DropdownItem>
        <DropdownItem onClick={() => onReboot(site)}>Reboot</DropdownItem>
        <DropdownItem onClick={() => onPerformance(site.id)}>Performance</DropdownItem>
        <DropdownItem divider />
        <DropdownItem
          className="list-action-danger"
          disabled={!isManager}
          onClick={() => onDelete(site.id, site.name)}
        >
          Delete
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const SiteList: React.FC = () => {
  const dispatch = useDispatch();
  
  // Use context for full data, but don't block initial render
  const { isLoading: contextLoading, isSuccess, isFailed } = useSiteContext();
  
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;

  const [page, setPage] = React.useState(1);

  // Use both cached and Redux data
  const { sites: cachedSites, controllers: cachedControllers } = useMapCache();
  const reduxSites = useSearch(useSelector(siteSelectors.selectAll));
  const controllerById = useSelector(controllerSelectors.selectEntities);
  const serverById = useSelector(serverSelectors.selectEntities);
  
  // Use Redux data if available, fallback to cache
  const sites = reduxSites.length > 0 ? reduxSites : cachedSites || [];

  //console.log('Sites',sites);

  const basePath = process.env.REACT_APP_BASE_PATH || '';
  const isManager = UserSession.getParam('role')?.toLowerCase() === 'manager';
  const partnerMetricsUrl = useSelector(selectMetricsUrl);

  const [siteStatuses, setSiteStatuses] = React.useState<Record<string, number>>({});
  const [statusLoading, setStatusLoading] = React.useState<Record<string, boolean>>({});


  // CACHED STATUS INITIALIZATION: Set status from cached data once
  React.useEffect(() => {
    if (cachedSites.length > 0 && Object.keys(siteStatuses).length === 0) {
      const newStatuses: Record<string, number> = {};
      const newLoading: Record<string, boolean> = {};
      
      cachedSites.forEach(site => {
        newStatuses[site.id] = site.status || 0;
        newLoading[site.id] = false;
      });
      
      setSiteStatuses(newStatuses);
      setStatusLoading(newLoading);
      
      if (process.env.NODE_ENV === 'development') {
        const validStatuses = Object.values(newStatuses).filter(s => s > 0).length;
      }
    }
  }, [cachedSites, siteStatuses]);

  //SITE LIST UPDATES: Handle new sites without overwriting status
  React.useEffect(() => {
    if (sites.length > 0) {
      const newStatuses = { ...siteStatuses };
      const newLoading = { ...statusLoading };
      let hasNewSites = false;
      
      sites.forEach(site => {
        if (!(site.id in newStatuses)) {
          // Only add status for new sites not already tracked
          const cachedSite = cachedSites.find(s => s.id === site.id);
          newStatuses[site.id] = cachedSite?.status || 0;
          newLoading[site.id] = false;
          hasNewSites = true;
        }
      });
      
      if (hasNewSites) {
        setSiteStatuses(newStatuses);
        setStatusLoading(newLoading);
        
        if (process.env.NODE_ENV === 'development') {
        }
      }
    }
  }, [sites, cachedSites, siteStatuses, statusLoading]);


  const sitessData = React.useMemo(() => sites, [sites]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  
  const [sorting, setSorting] = React.useState([
    {
      id: 'name',
      desc: false,
    },
  ]);
  

  const getController = React.useCallback((row) => {
    const gatewayId = row.original.Tunnels?.[0]?.Server?.gatewayId;

    if (!gatewayId || !controllerById || Object.keys(controllerById).length === 0) {
      return "N/A"; // If gatewayId is missing or controllerById is empty
    }

    const controller = controllerById[gatewayId];

    return controller && controller.name ? controller.name : "N/A";
  }, [controllerById]);


  const columns = React.useMemo(() => {
    const unorderedColumns = [
    {
      accessorKey: 'name',
      header: 'Name',
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.name || '';
        const bVal = rowB.original.name || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1; // Start → End after reversal
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1; // Direct to end
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      id: 'location',
      header: 'Location',
      accessorFn: (row) => [row.city, row.state].filter(Boolean).join(', '),
      cell: ({ row }) => [row.original.city, row.original.state].filter(Boolean).join(', ') || 'N/A',
      sortingFn: (rowA, rowB, columnId) => {
        const locationA = rowA.getValue(columnId) as string;
        const locationB = rowB.getValue(columnId) as string;
        const aEmpty = !locationA;
        const bEmpty = !locationB;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1; // Start → End after reversal
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1; // Direct to end
          if (bEmpty) return -1;
        }
        
        return locationA.localeCompare(locationB, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      accessorKey: 'ClientCompany.name',
      header: 'Company',
      cell: ({ row }) => (
        <>
          {row.original.ClientCompany && row.original.ClientCompany.name && (
            <strong>{row.original.ClientCompany.name}</strong>
          )}
        </>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.ClientCompany?.name || '';
        const bVal = rowB.original.ClientCompany?.name || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId || s.id === 'ClientCompany.name' || s.id === 'ClientCompany_name');
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1; // Start → End after reversal
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1; // Direct to end
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      accessorKey: 'controller',
      header: 'Controller',
      accessorFn: (row) => {
        const gatewayId = row.Tunnels?.[0]?.Server?.gatewayId;
        if (!gatewayId || !controllerById || Object.keys(controllerById).length === 0) {
          return "N/A";
        }
        const controller = controllerById[gatewayId];
        return controller && controller.name ? controller.name : "N/A";
      },
      cell: ({ row }) => (
        <div>
          {row.original.field2}
          <div>{getController(row)}</div>
        </div>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.getValue(columnId) as string;
        const bVal = rowB.getValue(columnId) as string;
        const aEmpty = !aVal || aVal === 'N/A';
        const bEmpty = !bVal || bVal === 'N/A';
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1; // Start → End after reversal
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1; // Direct to end
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableColumnFilter: false,
      accessorFn: (row) => {
        return siteStatuses[row.id] ?? 0;
      },
      cell: ({ row }) => {
        const siteId = row.original.id;
        if (statusLoading[siteId]) {
          return <Spinner size="sm" />;
        }
        const status = siteStatuses[siteId] ?? 0; // Default to 0 if undefined
        return (
          <Tooltip content={statusTexts[status] || statusTexts[0]}>
            <MdCircle color={statusColors[status] || statusColors[0]} />
          </Tooltip>
        );
      },
      sortingFn: (rowA, rowB, columnId) => {
        const statusA = siteStatuses[rowA.original.id] ?? 0;
        const statusB = siteStatuses[rowB.original.id] ?? 0;
        
        // Priority order: Black (0) → Red (1) → Yellow (2) → Gray (4) → Green (3)
        const priorityMap: Record<number, number> = {
          0: 0, // Black 
          1: 1, // Red 
          2: 2, // Yellow 
          4: 3, // Gray 
          3: 4, // Green
        };
        
        const priorityA = priorityMap[statusA] ?? 0;
        const priorityB = priorityMap[statusB] ?? 0;
        
        return priorityA - priorityB;
      },
    },
    {
      accessorKey: 'Wan.name',
      header: 'pWAN',
      enableColumnFilter: true,
      enableSorting: true,
      accessorFn: (row) => {
        return row.Wan?.name || '';
      },
      cell: ({ row }) => (
        <>
          {row.original.Wan && row.original.Wan.id && row.original.Wan.name && (
            <Link to={`${basePath}/wan/${row.original.Wan.id}`} className="wan-name">
              <strong>{row.original.Wan.name}</strong>
            </Link>
          )}
        </>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const wanA = rowA.getValue(columnId) as string;
        const wanB = rowB.getValue(columnId) as string;
        const aEmpty = !wanA;
        const bEmpty = !wanB;
        
        const currentSort = sorting.find(s => s.id === columnId || s.id === 'Wan.name' || s.id === 'Wan_name');
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1; // Start → End after reversal
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1; // Direct to end
          if (bEmpty) return -1;
        }
        
        return wanA.localeCompare(wanB, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      accessorKey: 'id',
      header: 'ID',
      size: 280,
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.id || '';
        const bVal = rowB.original.id || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1; // Start → End after reversal
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1; // Direct to end
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      id: 'actions',
      header: '',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <SiteActionsMenu
          site={row.original}
          basePath={basePath}
          isManager={isManager}
          onReboot={rebootSiteAlert}
          onPerformance={viewSitePerformance}
          onDelete={removeSiteAlert}
        />
      ),
    },
    ];

    const columnOrder = ['name', 'id', 'ClientCompany.name', 'controller', 'Wan.name', 'location', 'status', 'actions'];
    return columnOrder.map((columnId) => unorderedColumns.find((column) => (column.id || column.accessorKey) === columnId)!);
  }, [getController, siteStatuses, statusLoading, controllerById, sorting, basePath, isManager, partnerMetricsUrl]);

  const table = useReactTable({
    data: sitessData,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
  });

  const rebootSiteAlert = (site) => {
    Swal.fire({
      title: 'Confirm Reboot',
      html: `<div style="text-align: left;">
               <p>Are you sure you want to reboot this Site?</p>
               <strong>Name:</strong> ${site.name || 'N/A'}
             </div>`,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(actionCreators.siteRebootRequest(site.id));
      }
    });
  };

  const viewSitePerformance = (siteId) => {
    const envLink = process.env.REACT_APP_NOC_LINK || '';
    let performanceUrl = envLink.replace('{{site_id}}', siteId);

    if (partnerMetricsUrl) {
      try {
        const { pathname, search } = new URL(envLink);
        const metricsBaseUrl = partnerMetricsUrl.replace(/\/$/, '');
        performanceUrl = (metricsBaseUrl + pathname + search).replace('{{site_id}}', siteId);
      } catch {
        performanceUrl = envLink.replace('{{site_id}}', siteId);
      }
    }

    window.open(performanceUrl, '_blank');
  };

  const removeSiteAlert = (siteId, siteName) => {
    Swal.fire({
      title: 'Confirm Action',
      icon: 'warning',
      html: `<div style="text-align: left;">
               <p>Are you sure you want to delete this site?</p>
               <strong>Name:</strong> ${siteName || 'N/A'}
             </div>`,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteSiteAction(siteId);
      }
    });
  };

  const getColumns = () => {
    return [
      { title: 'Address', transforms: [sortable] },
      { title: 'City' },
      { title: 'Company' },
      { title: 'Controller', transforms: [sortable] },
      { title: 'pWAN', transforms: [sortable] },
      { title: 'ID' },
      { title: '' },
      { title: 'Actions', transforms: [cellWidth(10)] }
    ];
  }

  const editSite = React.useCallback((_, __, data) => {
    const siteId = data.cells[6].id;
    dispatch(push(`${basePath}/site/edit/${siteId}`));
  }, [dispatch]);

  const deleteSiteAction = React.useCallback((siteId) => {
    dispatch(actionCreators.siteDeleteRequest(siteId));
  }, [dispatch]);

  const siteFromItem = React.useCallback((item) => {
    const siteServerId = item.Tunnels?.[0]?.serverId ?? "N/A";
    const siteServer = siteServerId !== "N/A" && serverById ? serverById[siteServerId] : "N/A";
    
    // Check if controllerById exists and is not empty before accessing it
    let controllerName = "N/A";
    if (controllerById && Object.keys(controllerById).length > 0 && siteServer !== "N/A" && (siteServer as any)?.gatewayId) {
      const controller = controllerById[(siteServer as any)?.gatewayId];
      controllerName = controller && controller.name ? controller.name : "N/A";
    }
    
    return {
      cells: [
        { title: <Link to={`${basePath}/site/${item.id}`}>{item.name}</Link>},
        { title: item.city },
        { title: item.ClientCompany.name },
        { title: controllerName },
        { title: item.Wan ? <Link to={`${basePath}/wan/${item.Wan.id}`}>{item.Wan.name}</Link> : ""},
        { title: item.id.replaceAll('-', '') },
        { id: item.id }
      ]
    }
  }, [sites, serverById, controllerById]);

  // LOADING: Show error only if no cached data available
  if (isFailed && cachedSites.length === 0) {
    return (
      <React.Fragment>
        <Helmet>
          <title>Sites</title>
        </Helmet>
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 sites-list ${theme}`}
          title=""
          breadcrumbs={[{ name: 'Sites', active: true }]}
          actionButton={
            <Link to={`${basePath}/site/new`}>
              <Button className="ethica-button-green" size="sm">Add Site</Button>
            </Link>
          }
        >
          <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="sites-card-wrapper">
                <Card className="p-3">
                  <table className="sites-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>ID</th>
                        <th>Company</th>
                        <th>Controller</th>
                        <th>pWAN</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={8} className="text-center">No data available</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-end mt-3">
                    <Link to={`${basePath}/site/new`}>
                      <Button className="ethica-button-green" size="sm">Add Site</Button>
                    </Link>
                  </div>
                </Card>
              </div>
            </Col>
          </Row>
        </Page>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Sites</title>
      </Helmet>
      {contextLoading && cachedSites.length === 0 ? ( // Show loading only if no cached data
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 sites-list ${theme}`}
          title=""
          breadcrumbs={[{ name: 'Sites', active: true }]}
          actionButton={
            <Link to={`${basePath}/site/new`}>
              <Button className="ethica-button-green" size="sm">Add Site</Button>
            </Link>
          }
        >
          <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="sites-card-wrapper">
                <Card className="p-3">
                  <table className="sites-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th 
                              key={header.id} 
                              colSpan={header.colSpan}
                              style={{ 
                                padding: '8px',
                                whiteSpace: 'nowrap',
                                textAlign: header.column.getCanFilter() ? 'left' : 'center',
                                minWidth: header.column.id === 'id' ? '280px' : undefined,
                              }}
                            >
                              {header.isPlaceholder ? null : (
                                <div className={`d-flex  align-items-center ${header.column.getCanFilter() ? 'justify-content-start' : 'justify-content-center'}`}>
                                  <div
                                    {...{
                                      className: header.column.getCanSort()
                                        ? 'select-none table-column-header'
                                        : 'table-column-header',
                                      onClick: header.column.getToggleSortingHandler(),
                                    }}
                                  >
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                    {header.column.getCanSort() && (
                                      {
                                        asc: <MdArrowUpward className="ms-2" />,
                                        desc: <MdArrowDownward className="ms-2" />,
                                      }[header.column.getIsSorted() as string] ?? (
                                        <MdUnfoldMore className="ms-2 opacity-50" />
                                      )
                                    )}
                                  </div>
                                  {header.column.getCanFilter() ? (
                                    <div className="d-flex justify-content-end">
                                      <Filter column={header.column} />
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map(row => (
                        <React.Fragment key={row.id}>
                            <tr>
                            {row.getVisibleCells().map(cell => (
                              <td 
                                key={cell.id} 
                                className={`${cell.column.getCanFilter() ? '' : 'text-center'} ${cell.column.id === 'actions' ? 'actions-column' : ''}`}
                                style={{ 
                                  padding: '8px',
                                  fontSize: '12px',
                                  textAlign: cell.column.columnDef.header === 'Name' ? 'left' : (cell.column.getCanFilter() ? 'left' : 'center'),
                                  whiteSpace: 'nowrap',
                                  overflow: cell.column.id === 'actions' ? 'visible' : 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: cell.column.columnDef.size || 'auto',
                                  minWidth: cell.column.id === 'id' ? '280px' : undefined,
                                  position: cell.column.id === 'actions' ? 'relative' : undefined,
                                  zIndex: cell.column.id === 'actions' ? 2 : undefined,
                                }}
                              >
                                {cell.column.columnDef.header === 'Name' ? (
                                  <>
                                    <strong>
                                      <Link to={`${basePath}/site/${row.original.id}`} className="site-name">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                      </Link>
                                    </strong>
                                  </>
                                ) : (
                                  flexRender(cell.column.columnDef.cell, cell.getContext())
                                )}
                              </td>
                            ))}
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  {/* Пагинация */}
                  <div className="d-flex items-center justify-content-center align-items-center flex-wrap mt-2">
                    <div className="me-2">
                      <span className="flex items-center gap-1">
                        Go to page:
                        <input
                          type="number"
                          min="1"
                          max={table.getPageCount()}
                          defaultValue={table.getState().pagination.pageIndex + 1}
                          onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0
                            table.setPageIndex(page)
                          }}
                          className="border p-1 rounded w-16"
                        />
                      </span>
                    </div>
                    <div>
                        <button
                          className="border rounded p-1 me-1"
                          onClick={() => table.setPageIndex(0)}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {'<<'}
                        </button>
                        <button
                          className="border rounded p-1 me-1"
                          onClick={() => table.previousPage()}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {'<'}
                        </button>
                        <span className="flex items-center me-1">
                          <strong>
                            {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
                          </strong>
                        </span>
                        <button
                          className="border rounded p-1 me-1"
                          onClick={() => table.nextPage()}
                          disabled={!table.getCanNextPage()}
                        >
                          {'>'}
                        </button>
                        <button
                          className="border rounded p-1 me-2"
                          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                          disabled={!table.getCanNextPage()}
                        >
                          {'>>'}
                        </button>
                      </div>
                      <div>
                        <select
                          className="form-select"
                          value={table.getState().pagination.pageSize}
                          onChange={e => {
                            table.setPageSize(Number(e.target.value))
                          }}
                        >
                          {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                              Show {pageSize}
                            </option>
                          ))}
                        </select>
                      </div>
                  </div>
                </Card>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} sm={12} xs={12}>
              <div className="sites-card-wrapper site-map">
                <h2><strong>Sites Map</strong></h2>
                <Card inverse style={{background: '#808080'}}>
                  <GoogleMapWithBubblesSites />
                </Card>
              </div>
            </Col>
          </Row>

        </Page>
      )}
    </React.Fragment>
  )
}

interface CustomColumnMeta {
  filterVariant?: 'range' | 'select' | 'text';
}

function Filter({ column }: { column: Column<any, unknown> & { columnDef: { meta?: CustomColumnMeta } } }): JSX.Element {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <DebouncedInput
      className="form-control"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
    // See faceted column filters example for datalist search suggestions
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}
